'use client';

import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { Accordion, AccordionDetails, AccordionSummary, Stack, Typography } from '@mui/material';
import Image from 'next/image';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

import { icons } from '@/assets/icons';
import { OUR_SERVICES } from '@/global/constants/common';

import HexagonalButton from './bases/HexagonalButton';

export const OurServicesSection = () => {
  const [expanded, setExpanded] = useState<string | false>(false);
  const router = useRouter();
  const searchParams = useSearchParams();

  const handleChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  useEffect(() => {
    const serviceSlug = searchParams.get('service');
    if (!serviceSlug) return;

    const slugToLabel: Record<string, string> = {
      'advisory-services': 'Advisory Services',
      'community-management': 'Community Management',
      'customer-support-education': 'Customer Support & Education',
      'exchange-listing-strategy': 'Exchange Listing Strategy',
      'live-streaming-services': 'Live Streaming Services',
      'market-making-services': 'Market Making Services',
      'marketing-services': 'Marketing Services',
    };

    const label = slugToLabel[serviceSlug];
    if (!label) return;

    const targetIndex = OUR_SERVICES.findIndex(s => s.question === label);
    if (targetIndex < 0) return;

    const panelId = `panel-${targetIndex}`;
    setExpanded(panelId);

    setTimeout(() => {
      const element = document.getElementById(panelId);
      if (element) {
        const headerOffset = 100;
        const rect = element.getBoundingClientRect();
        const offsetTop = rect.top + window.scrollY - headerOffset;
        window.scrollTo({ top: offsetTop, behavior: 'smooth' });
      }
    }, 100);
  }, [searchParams]);

  return (
    <Stack
      width={'100%'}
      height={'100%'}
      flexGrow={1}
      alignItems={'center'}
      component={'section'}
      position={'relative'}>
      <Stack padding={{ xs: '100px 24px 40px', sm: '120px 80px 80px' }} alignItems={'center'}>
        <Stack gap={{ xs: 3, sm: 7 }} width={'min(1280px, 100%)'} zIndex={2} alignItems={'center'}>
          <Typography
            component={'h2'}
            variant={'displayMedium'}
            className={'clip-text'}
            textAlign={'center'}
            whiteSpace={'pre-line'}
            fontSize={{ xs: '24px', sm: '48px' }}
            lineHeight={{ xs: '32px', sm: '68px' }}
            sx={{ background: 'linear-gradient(90deg, #A9A9A9 0%, #FFFFFF 50%, #A9A9A9 100%)' }}>
            OUR SERVICES
          </Typography>
          <Stack width={'min(838px, 100%)'}>
            {OUR_SERVICES.map((item, index) => {
              const panel = `panel-${index}`;
              const isDevelopmentServices = item.question === 'Development Services';
              return (
                <Accordion
                  id={panel}
                  key={index.toString()}
                  expanded={expanded === panel}
                  onChange={handleChange(panel)}
                  sx={{
                    '&.MuiAccordion-root': {
                      borderRadius: 0,
                      backgroundColor: 'transparent',
                      padding: { xs: '24px 0px', sm: '40px 0px' },
                      borderBottom: '1px solid #FFFFFF33',
                      '&.Mui-expanded': {
                        margin: 0,
                      },
                    },
                  }}>
                  <AccordionSummary
                    expandIcon={
                      expanded === panel ? (
                        <RemoveIcon style={{ color: '#FFF' }} />
                      ) : (
                        <AddIcon style={{ color: '#FFF' }} />
                      )
                    }
                    sx={{
                      '&.MuiAccordionSummary-root': {
                        padding: 0,
                      },
                    }}>
                    <Typography
                      fontSize={{ xs: '18px', sm: '24px' }}
                      component={'span'}
                      variant={'headLarge2'}
                      color={'#FFF'}>{`${item.question}`}</Typography>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      '&.MuiAccordionDetails-root': {
                        padding: 0,
                      },
                    }}>
                    <Typography
                      fontSize={{ xs: '14px', sm: '18px' }}
                      variant={'bodyLarge'}
                      color={'#FFFFFF'}
                      className={'mix-blend'}>
                      {item.answer}
                    </Typography>
                    {isDevelopmentServices && (
                      <HexagonalButton
                        width={390}
                        variant={'primary'}
                        label='EXPLORE DEVELOPMENT SERVICES'
                        labelClassName={'xs:text-[24px]'}
                        className='mt-8 md:mt-10'
                        onClick={() => {
                          router.push('/services/development-services');
                        }}
                      />
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Stack>
        </Stack>
      </Stack>
      <Stack zIndex={1} position={'absolute'} top={0} left={0} right={0} bottom={0}>
        <Image src={icons.ic_wave_section} alt={''} layout={'fill'} objectFit={'cover'} />
      </Stack>
    </Stack>
  );
};
